import { useMutationState } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { PendingCartMutation } from './types';

import {
  MUTATION_CART_ADD_LINE_ITEMS_KEY,
  MUTATION_CART_DELETE_LINE_ITEMS_KEY,
  MUTATION_CART_UPDATE_LINE_ITEMS_KEY,
} from '$src/constants';

import { ZCartMutationKey, selectPendingMutation } from './validator';

/**
 * Tracks the state of the cart line items mutations.
 * We consider mutations of type `add`, `update` and `delete` as ongoing mutations.
 * @returns The state of the cart line items mutations.
 * @property ongoingMutationCount - The number of ongoing mutations.
 * @property isCartMutating - Whether the cart has any ongoing mutations.
 * @property isItemMutating - Whether a specific item in the cart is mutating.
 * @property pendingAdditions - The number of pending `add` mutations.
 */
export const useCartLineItemsMutatingState = () => {
  const pendingMutations = useMutationState({
    filters: {
      status: 'pending',
      predicate: (mutation) => ZCartMutationKey.safeParse(mutation.options.mutationKey?.[0]).success,
    },
    select: (mutation) => selectPendingMutation(mutation),
  });

  const typedMutations = pendingMutations.filter((m): m is PendingCartMutation => m !== null);
  const ongoingMutationCount = typedMutations.length;

  const { mutatingItemIds, pendingAdditions } = typedMutations.reduce(
    (acc, mutation) => {
      switch (mutation.kind) {
        case MUTATION_CART_ADD_LINE_ITEMS_KEY:
          acc.pendingAdditions += mutation.variables.lineItems.length;
          break;
        case MUTATION_CART_UPDATE_LINE_ITEMS_KEY:
          for (const item of mutation.variables.lineItems) acc.mutatingItemIds.add(item.id);
          break;
        case MUTATION_CART_DELETE_LINE_ITEMS_KEY:
          for (const id of mutation.variables.lineItemIds) acc.mutatingItemIds.add(id);
          break;
      }
      return acc;
    },
    { mutatingItemIds: new Set<string>(), pendingAdditions: 0 },
  );

  const isItemMutating = useCallback(
    (itemId: string): boolean => mutatingItemIds.has(itemId),
    [mutatingItemIds],
  );

  return { ongoingMutationCount, isCartMutating: ongoingMutationCount > 0, isItemMutating, pendingAdditions };
};
